import { VideoCollectionVideo } from '@/scripts/models/VideoCollectionVideo';
import { Nullable } from '@/scripts/models/utility-types';
import {
  StoreModule,
  provideMapGetter,
  provideMapState,
  provideMapMutation,
  provideMapAction,
} from '@/scripts/store/helpers/createStore';
import { State } from './state';
import { Getters } from './getters';
import { Actions } from './actions';
import { Mutations } from './mutations';
import { setVideoPlayerError } from './mutations/setVideoPlayerError';
import { initializeVideoPage } from './controllers/initializer';
import { fallbackOnVideoSource } from './controllers/initiaizeVideoSource';
import { checkContentWatchability } from './controllers/contentWatchabilityController';
import {
  add as addVideoPlayerOverlay,
  remove as removeVideoPlayerOverlay,
  removeAll as removeAllVideoPlayerOverlay,
} from './mutations/manageVideoPlayerOverlays';
import { VideoOverlay } from './state/VideoOverlay';

export const storeNamespace = 'video';
export const storeModule: StoreModule<State, Getters, Mutations, Actions, any, any> = {
  namespaced: true,
  state: {
    canWatch: null,
    video: null,
    token: null,
    videoSource: null,
    videoPlayerOptions: null,

    error: null,
    overlays: [],

    watchNextVideo: null,
  },
  mutations: {
    setVideo(state, video): void {
      state.video = video;
    },

    setToken(state, token): void {
      state.token = token;
    },

    setVideoSource(state, videoSource): void {
      state.videoSource = videoSource;
    },

    setCanWatch(state, canWatch): void {
      state.canWatch = canWatch;
    },

    setVideoPlayerOptions(state, options = {}): void {
      state.videoPlayerOptions = options;
    },

    setVideoPlayerError,

    addVideoPlayerOverlay,
    removeVideoPlayerOverlay,
    removeAllVideoPlayerOverlay,

    setWatchNextVideo(state, videoCollectionVideo): void {
      state.watchNextVideo = videoCollectionVideo;
    },
  },
  getters: {
    nextVideo: (state): Nullable<VideoCollectionVideo> => {
      if (state.watchNextVideo) {
        return state.watchNextVideo;
      }
      return null;
    },
    activeOverlay: (state): Nullable<VideoOverlay> => state.overlays[0] ?? null,
  },
  actions: {
    init(context, { video, options, token }): Promise<void> {
      const { commit } = context;
      commit('setVideo', video);
      commit('setToken', token);

      commit('setVideoPlayerOptions', options || {});

      return initializeVideoPage(context);
    },

    addWatchNextVideo({ commit, state }, {
      videoCollectionVideo,
      skipIfExist,
    }): void {
      if (skipIfExist && !!state.watchNextVideo) return;

      commit('setWatchNextVideo', videoCollectionVideo);
    },

    handleVideoSourceError: fallbackOnVideoSource,

    checkContentWatchability,
  },
};

export const mapState = provideMapState<State>(storeNamespace);
export const mapGetter = provideMapGetter<Getters>(storeNamespace);
export const mapMutation = provideMapMutation<Mutations>(storeNamespace);
export const mapAction = provideMapAction<Actions>(storeNamespace);
